import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, TriangleAlert, ArrowUpRight } from 'lucide-react';
import { financeService } from '../../services/financeService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { usePeriodDrilldown } from '../../hooks/usePeriodDrilldown';

// =============================================================================
// Finance › Depreciation   (LIVE)
//
// MOVED HERE OUT OF CAPITAL, and the old page it replaces was a mockup on six
// hard-coded assets.
//
// It belongs in Finance because everything on it is a FINANCIAL POLICY
// decision: what an asset cost, how long it is written off over, what method,
// and which account the charge lands in. None of that is a maintenance
// question. Capital keeps the Asset Register — the physical list of what
// exists, where it is and what it would cost to replace — and the two are
// deliberately different lists about the same objects.
//
// TWO SOURCES, ONE SCHEDULE. The charge covers assets already owned AND
// budgeted capex, because an asset bought in month 4 depreciates for the rest
// of the year. A schedule that only knew about what was already owned would
// understate the charge every time the business invests, which is exactly when
// somebody is looking at it.
//
// NULL MEANS "TAKE THE CLASS DEFAULT", NOT ZERO. That is what makes a
// hundred-asset register maintainable: set the policy once on the class, and
// override only the assets that genuinely differ. The grid shows an inherited
// value in muted text so you can see at a glance what has been overridden and
// what is following policy.
//
// THE CHARGE POSTS TO THE SPINE, so the P&L reads it, the cash flow adds it
// back as a non-cash item and the balance sheet depreciates PPE by it — all
// from one number. This page does not recompute it for display; it reports the
// same figure the statements use, which is the only way those three can never
// disagree.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';

const fmtR = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v === 0) return '—';
  return Math.abs(v).toLocaleString();
};

const T_CAPEX = { bgCell: 'rgba(37, 99, 235, 0.09)' };
const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.18)', baseFontStyle: '700 12px' };
const T_YEAR = { bgCell: 'rgba(100, 116, 139, 0.10)' };
const T_MUTED = { textDark: '#94a3b8' };

const METHODS = [
  ['', 'Class default'],
  ['STRAIGHT_LINE', 'Straight line'],
  ['REDUCING_BALANCE', 'Reducing balance'],
  ['NONE', 'Does not depreciate'],
];
const METHOD_LABEL = Object.fromEntries(METHODS.map(([v, l]) => [v, l]));

const EMPTY = { items: [], schedule: [], unusable: [] };

export default function Depreciation() {
  const navigate = useNavigate();
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getDepreciation();
      setData(res.data || EMPTY);
      setEdits({});
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the depreciation schedule.');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  // ---- Rows: items + their twelve periods ----------------------------------
  const rows = useMemo(() => {
    const byItem = new Map();
    (data.schedule || []).forEach((s) => {
      const k = `${s.item_kind}|${s.item_code}`;
      if (!byItem.has(k)) byItem.set(k, Array(12).fill(0));
      if (s.financial_period >= 1 && s.financial_period <= 12) {
        byItem.get(k)[s.financial_period - 1] += s.budget_amount;
      }
    });

    const out = (data.items || []).map((i) => {
      const k = `${i.item_kind}|${i.item_code}`;
      const e = edits[i.item_code] || {};
      return {
        ...i,
        ...e,
        key: k,
        dirty: Object.keys(e).length > 0,
        values: byItem.get(k) || Array(12).fill(0),
      };
    });

    // Sort: capex last, so the register you can edit reads first and the
    // additions that arrive during the year sit visibly apart from it.
    out.sort((a, b) => (a.item_kind === b.item_kind
      ? String(a.item_code).localeCompare(String(b.item_code))
      : (a.item_kind === 'ASSET' ? -1 : 1)));

    const totals = Array(12).fill(0);
    out.forEach((r) => r.values.forEach((v, i) => { totals[i] += v; }));
    out.push({ key: '__total', isTotal: true, description: 'Total depreciation charge', values: totals });
    return out;
  }, [data.items, data.schedule, edits]);

  const yearTotal = useMemo(
    () => rows.find((r) => r.isTotal)?.values.reduce((a, b) => a + b, 0) || 0,
    [rows]
  );

  const {
    visibleCols, yearExpanded, expandedQuarters,
    toggleYear, toggleQuarter, expandAll, collapseAll,
  } = usePeriodDrilldown(PERIODS);

  // Item, description, class, cost, in service, life, method, accumulated.
  const FIXED = 8;

  const gcols = useMemo(() => {
    const dyn = visibleCols.map((c) => {
      if (c.kind === 'year') return { id: 'YEAR', title: `${yearExpanded ? '▾' : '▸'} Full Year`, width: 124 };
      if (c.kind === 'quarter') return { id: `Q${c.q}`, title: `${expandedQuarters[c.q] ? '▾' : '▸'} Q${c.q}`, width: 104 };
      return { id: `P${c.period}`, title: periodLabel(c.period, fyStartMonth), width: 88 };
    });
    return [
      { id: 'code', title: 'Item', width: 150 },
      { id: 'desc', title: 'Description', width: 240, grow: 1 },
      { id: 'class', title: 'Class', width: 120 },
      { id: 'cost', title: 'Cost', width: 120 },
      { id: 'inservice', title: 'In Service', width: 100 },
      { id: 'life', title: 'Life (mths)', width: 100 },
      { id: 'method', title: 'Method', width: 150 },
      { id: 'accum', title: 'Accum. Depn', width: 124 },
      ...dyn,
    ];
  }, [visibleCols, yearExpanded, expandedQuarters, fyStartMonth]);

  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = rows[row];
    if (!r) return gcell.text('', { readonly: true });

    if (r.isTotal) {
      if (id === 'desc') return gcell.text(r.description.toUpperCase(), { readonly: true, theme: T_TOTAL });
      const d = visibleCols[col - FIXED];
      if (!d) return gcell.text('', { readonly: true, theme: T_TOTAL });
      const v = d.kind === 'month' ? r.values[d.period - 1] : d.periods.reduce((a, p) => a + r.values[p - 1], 0);
      return gcell.number(v, { display: fmtR(v), readonly: true, theme: T_TOTAL });
    }

    // CAPEX rows are read-only HERE and editable on the Capex page. The
    // alternative — editing a capex line's life from two places — is how the
    // schedule and the capex plan end up disagreeing.
    const isCapex = r.item_kind === 'CAPEX';
    const base = isCapex ? T_CAPEX : undefined;
    const editable = !isCapex;

    switch (id) {
      case 'code':
        return gcell.text(`${isCapex ? '◆ ' : ''}${r.item_code}`, { readonly: true, theme: base });
      case 'desc':
        return gcell.text(r.description || '', { readonly: true, theme: base });
      case 'class':
        return gcell.text(r.asset_class_code || '', { readonly: true, theme: base || T_MUTED });
      case 'cost':
        return gcell.number(r.cost, {
          display: r.cost === null ? 'not set' : fmtR(r.cost),
          readonly: !editable, theme: base,
        });
      case 'inservice':
        return gcell.text(r.in_service_period ? `P${r.in_service_period}` : '—',
          { readonly: true, theme: base || T_MUTED });
      case 'life':
        return gcell.number(r.useful_life_months, {
          display: r.useful_life_months === null ? 'class default' : String(r.useful_life_months),
          readonly: !editable,
          // Muted when inherited, so overrides are visible at a glance.
          theme: r.useful_life_months === null ? (base || T_MUTED) : base,
        });
      case 'method':
        return gcell.text(METHOD_LABEL[r.depreciation_method || ''] || r.depreciation_method || 'Class default', {
          readonly: !editable,
          theme: r.depreciation_method ? base : (base || T_MUTED),
        });
      case 'accum':
        return gcell.number(r.accum_opening, {
          display: r.accum_opening === null ? '—' : fmtR(r.accum_opening),
          readonly: !editable, theme: base,
        });
      default: {
        const d = visibleCols[col - FIXED];
        if (!d) return gcell.text('', { readonly: true, theme: base });
        const v = d.kind === 'month'
          ? (r.values[d.period - 1] || 0)
          : d.periods.reduce((a, p) => a + (r.values[p - 1] || 0), 0);
        return gcell.number(v, {
          display: fmtR(v), readonly: true,
          theme: base || (d.kind === 'month' ? undefined : T_YEAR),
        });
      }
    }
  }, [colIds, rows, visibleCols, FIXED]);

  const rowsRef = useRef([]);
  rowsRef.current = rows;

  const onCellEdited = useCallback(([col, row], newValue) => {
    const id = colIds[col];
    const r = rowsRef.current[row];
    if (!r || r.isTotal || r.item_kind === 'CAPEX') return;

    const map = { cost: 'acquisition_cost', life: 'useful_life_months', accum: 'accumulated_depreciation_opening' };
    const field = map[id];
    if (!field) return;

    const v = newValue?.data;
    setEdits((prev) => ({
      ...prev,
      [r.item_code]: {
        ...(prev[r.item_code] || {}),
        [field === 'acquisition_cost' ? 'cost' : field === 'accumulated_depreciation_opening' ? 'accum_opening' : field]:
          v === '' || v === null || v === undefined ? null : Number(v),
      },
    }));
  }, [colIds]);

  const onHeaderClicked = useCallback((colIndex) => {
    const id = colIds[colIndex];
    if (id === 'YEAR') toggleYear();
    else if (/^Q\d+$/.test(id)) toggleQuarter(Number(id.slice(1)));
  }, [colIds, toggleYear, toggleQuarter]);

  const dirtyCodes = Object.keys(edits);

  const saveAll = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      for (const code of dirtyCodes) {
        const item = (data.items || []).find((i) => i.item_code === code) || {};
        const e = edits[code];
        await financeService.saveAssetFinancials(code, {
          acquisition_cost: 'cost' in e ? e.cost : item.cost,
          useful_life_months: 'useful_life_months' in e ? e.useful_life_months : item.useful_life_months,
          accumulated_depreciation_opening: 'accum_opening' in e ? e.accum_opening : item.accum_opening,
          residual_value: item.residual_value,
          reducing_rate_pct: item.reducing_rate_pct,
          depreciation_method: item.depreciation_method,
          acquisition_date: item.in_service_date,
          gl_depreciation_expense_account: item.gl_depreciation_expense_account,
          gl_accum_depreciation_account: item.gl_accum_depreciation_account,
          gl_asset_cost_account: item.gl_asset_cost_account,
        });
      }
      setNotice(`Saved ${dirtyCodes.length} asset${dirtyCodes.length === 1 ? '' : 's'}. The P&L, cash flow and balance sheet all move with it.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const assetCount = (data.items || []).filter((i) => i.item_kind === 'ASSET').length;
  const capexCount = (data.items || []).filter((i) => i.item_kind === 'CAPEX').length;

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to see the depreciation schedule.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate('/finance')}>
              <ArrowLeft className="w-3.5 h-3.5" /> Finance
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              Depreciation
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
            {data.unusable.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-semibold dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
                <TriangleAlert className="w-3 h-3" />
                {data.unusable.length} cannot depreciate
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Annual charge</span>
              <span className="text-sm font-bold tabular-nums">{fmtR(yearTotal)}</span>
            </div>
            {dirtyCodes.length > 0 && (
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900"
                      onClick={saveAll} disabled={saving}>
                {saving ? 'Saving…' : `Save ${dirtyCodes.length} change${dirtyCodes.length === 1 ? '' : 's'}`}
              </button>
            )}
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {error && (
          <div className="rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-emerald-700 dark:text-emerald-300">{notice}</p>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows.length <= 1 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              Nothing depreciates in {selectedVersion}. Assets need a cost and a useful life before they produce a charge.
            </p>
          ) : (
            <MantleDataGrid
              gridId="finance-depreciation"
              fitHeight
              fitHeightPadding={6}
              columns={gcols}
              rowCount={rows.length}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onHeaderClicked={onHeaderClicked}
              freezeColumns={2}
              hint="Cost, life and accumulated depreciation are editable on ASSET rows. Blank life = the class default. ◆ rows are budgeted capex — edit those on the Capex page so the schedule and the capex plan cannot disagree."
              toolbar={
                <>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    {assetCount} asset{assetCount === 1 ? '' : 's'} · {capexCount} capex addition{capexCount === 1 ? '' : 's'}
                  </span>
                  <button type="button" onClick={yearExpanded ? collapseAll : expandAll} className={btn}>
                    {yearExpanded ? 'Collapse periods' : 'Expand periods'}
                  </button>
                  <button type="button" className={btn} onClick={() => navigate('/capital/assets')}>
                    Asset Register <ArrowUpRight className="w-3 h-3" />
                  </button>
                </>
              }
            />
          )}
        </div>

        {/* The gap that a grid of numbers cannot show you: assets that will
            never produce a charge. "Depreciation looks low" is not a diagnosis
            anybody can act on; "these four have no cost" is. */}
        {data.unusable.length > 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
              <TriangleAlert className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
                {data.unusable.length} asset{data.unusable.length === 1 ? '' : 's'} marked depreciable but producing no charge
              </h2>
            </div>
            <ul className="m-0 p-0 list-none divide-y divide-slate-100 dark:divide-slate-700/60 max-h-64 overflow-y-auto">
              {data.unusable.map((u) => {
                const why = [
                  u.no_cost && 'no acquisition cost',
                  u.no_life && 'no useful life (and none on its class)',
                  u.no_method && 'no depreciation method',
                  u.no_account && 'no depreciation expense account',
                ].filter(Boolean);
                return (
                  <li key={u.asset_code} className="px-4 py-2 border-l-4 border-l-amber-400 bg-amber-50/40 dark:bg-amber-500/5">
                    <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">
                      {u.asset_code} <span className="font-normal text-slate-500 dark:text-slate-400">{u.description}</span>
                    </p>
                    <p className="m-0 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                      {why.join(' · ')}
                      {u.asset_class_code ? ` — class ${u.asset_class_code}` : ' — no class set'}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          <strong>This is the same number the statements use.</strong> The charge posts to the consolidated ledger,
          so the P&amp;L reads it, the cash flow adds it back as a non-cash item and the balance sheet depreciates
          PPE by it. Nothing here is recalculated for display &mdash; that is the only way the three can never
          disagree.
        </p>
        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          <strong>Policy lives on the class.</strong> A blank life or method means &ldquo;use the class
          default&rdquo;, shown in muted text, so an override is visible at a glance. Set the defaults in Master
          Data &rsaquo; Asset Classes; the physical register &mdash; what exists, where it is, what it would cost
          to replace &mdash; stays in <button className="underline hover:text-slate-600 dark:hover:text-slate-300" onClick={() => navigate('/capital/assets')}>Capital &rsaquo; Asset Register</button>.
        </p>
      </div>
    </div>
  );
}
